import * as React from 'react';
import { MediaQuery, Link, DateTime, Image, Util, Button, Carousel, LazyImage, Accordion, AccordionItem, SkeletonLoader } from '@grandvision/common';
import { Container, Heading, Text } from '@grandvision/elements';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Link as Link$1 } from 'react-scroll';
import { Icon } from '@grandvision/icons';
import { parseHtml } from '@grandvision/utils';
import { useIntl } from '@grandvision/state';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var Picture = function (_a) {
    var url = _a.url, mobileUrl = _a.mobileUrl, alt = _a.alt, className = _a.className;
    return (React.createElement("picture", { className: classnames('picture', className) },
        mobileUrl && (React.createElement("source", { media: "(max-width: " + MediaQuery.Breakpoints.M + "px)", srcSet: mobileUrl })),
        React.createElement("source", { media: "(min-width: " + MediaQuery.Breakpoints.M + ")", srcSet: url }),
        React.createElement("img", { className: "picture__image", src: url, alt: alt })));
};

var LazyPicture = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, className = _a.className;
    var isPhone = MediaQuery.useIsPhone();
    var _b = isPhone && mobileImage ? mobileImage.dimensions : image.dimensions, h = _b.h, w = _b.w;
    var _c = __read(useInView({
        rootMargin: '150px 0px',
        triggerOnce: true,
    }), 2), ref = _c[0], inView = _c[1];
    return (React.createElement("div", { ref: ref, className: classnames('lazy-picture', { 'lazy-picture--loaded': inView }, className), style: { paddingBottom: ((h / w) * 100).toFixed(3) + "%" } }, inView && (React.createElement(Picture, { className: "lazy-picture__image", mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url, url: image.url, alt: image.alt }))));
};

var Banner = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, _b = _a.useContainer, useContainer = _b === void 0 ? false : _b, _c = _a.useLazyLoading, useLazyLoading = _c === void 0 ? true : _c;
    var ContainerComponent = useContainer ? Container : React.Fragment;
    if (!image) {
        console.warn('Cannot render Banner: missing image');
        return null;
    }
    return (React.createElement("div", { className: "banner" },
        React.createElement(ContainerComponent, null,
            useLazyLoading ? (React.createElement(LazyPicture, { mobileImage: mobileImage, image: image })) : (React.createElement(Picture, { mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url, url: image.url, alt: image.alt })),
            link && (React.createElement(Link, { hidden: true, url: link.url, target: link.target, className: "banner__link" }, link.label && React.createElement("span", { className: "banner__link-text" }, link.label))))));
};

var BlogMeta = function (_a) {
    var publishDate = _a.publishDate, _b = _a.readingTime, readingTime = _b === void 0 ? 0 : _b, minutesLabel = _a.minutesLabel, locale = _a.locale;
    var hasReadingTime = readingTime && readingTime > 0 && minutesLabel;
    if (!publishDate) {
        return null;
    }
    return (React.createElement("div", { className: "blog-meta" },
        React.createElement("span", { className: "blog-meta__date" },
            React.createElement(DateTime, { dateTime: publishDate, locale: locale })),
        hasReadingTime && (React.createElement("span", { className: "blog-meta__minutes" }, "- " + readingTime.toString() + " " + minutesLabel))));
};

var BlogCard = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, locale = _a.locale, readingTime = _a.readingTime, publishDate = _a.publishDate, summary = _a.summary, url = _a.url, minutesLabel = _a.minutesLabel;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    if (!image && !mobileImage && !title && !summary) {
        console.error('BlogCard: missing content, please supply an image, mobileImage, title or summary');
        return null;
    }
    return (React.createElement("a", { className: "blog-card", href: url },
        React.createElement("div", { className: "blog-card__content" },
            image && imageUrl && (React.createElement(Image, { className: "blog-card__image", url: imageUrl, alt: image.alt, cover: true })),
            React.createElement("div", { className: "blog-card__text" },
                title && (React.createElement(Heading, { component: "h3", className: "blog-card__title" }, Util.truncateString(title, 50))),
                summary && React.createElement("p", { className: "blog-card__paragraph" }, Util.truncateString(summary, 200)),
                React.createElement(BlogMeta, { publishDate: publishDate, readingTime: readingTime, minutesLabel: minutesLabel, locale: locale })))));
};
var BlogCard$1 = React.memo(BlogCard);

// fixed navigation height needed for anchor scrolling
var siteNavHeightOffset = -64;
var BlogDetailHeader = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, scrollTo = _a.scrollTo;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image.url;
    if (!imageUrl) {
        return null;
    }
    return (React.createElement("div", { className: "blog-detail-header", style: { backgroundImage: "url(" + imageUrl + ")" } },
        React.createElement("h1", { className: "blog-detail-header__title" }, title),
        React.createElement(Link$1, { className: "blog-detail-header__link", href: "#", to: scrollTo, offset: siteNavHeightOffset, smooth: true, duration: 500 },
            React.createElement(Icon, { name: "chevron-down" }))));
};

var BlogHero = function (_a) {
    var _b;
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, summary = _a.summary, link = _a.link, readMoreLabel = _a.readMoreLabel;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var imageUrl = isSmallScreen && mobileImage ? mobileImage.url : (_b = image === null || image === void 0 ? void 0 : image.url) !== null && _b !== void 0 ? _b : '';
    var textLength = isSmallScreen ? 100 : 150;
    return (React.createElement("div", { className: "blog-hero", style: { backgroundImage: "url(" + imageUrl + ")" } },
        React.createElement(Container, { className: "site-container" },
            React.createElement("div", { className: "blog-hero__content" },
                React.createElement(Heading, { component: "h1", className: "blog-hero__title" }, title),
                summary && (React.createElement(Text, { className: "blog-hero__intro" }, Util.truncateString(summary, textLength))),
                React.createElement(Button, { size: "medium", href: link, className: "blog-hero__button", variant: "outline", caption: readMoreLabel, "aria-label": readMoreLabel })))));
};
var BlogHero$1 = React.memo(BlogHero);

var BlogIntro = function (_a) {
    var summary = _a.summary, publishDate = _a.publishDate, readingTime = _a.readingTime, minutesLabel = _a.minutesLabel, locale = _a.locale;
    return (React.createElement("div", { className: "blog-intro" },
        summary && React.createElement("p", { className: "blog-intro__summary" }, summary),
        React.createElement(BlogMeta, { publishDate: publishDate, readingTime: readingTime, minutesLabel: minutesLabel, locale: locale })));
};

var BlogList = function (_a) {
    var title = _a.title, children = _a.children, noArticlesLabel = _a.noArticlesLabel;
    return (React.createElement("div", { className: "blog-list" },
        React.createElement(Heading, { component: "h2", className: "blog-list__title" }, title),
        children ? (React.createElement("div", { className: "blog-list__wrapper" }, children)) : (React.createElement(Heading, { variant: "smallest", className: "blog-list__message" }, noArticlesLabel))));
};

var BrandList = function (_a) {
    var title = _a.title, children = _a.children, className = _a.className;
    return (React.createElement("div", { className: classnames('brand-list', className) },
        React.createElement("h3", { className: "heading brand-list__title" }, title),
        React.createElement("ul", { className: "brand-list__content" }, children)));
};

var BrandListGroup = function (_a) {
    var title = _a.title, children = _a.children, className = _a.className;
    return (React.createElement("li", { className: classnames('brand-list-group', className) },
        React.createElement("h5", { className: classnames('brand-list-group__title', {
                'brand-list-group__title--empty': !(title === null || title === void 0 ? void 0 : title.length),
            }) }, title),
        children));
};

var BrandListItem = function (_a) {
    var url = _a.url, children = _a.children, className = _a.className;
    return (React.createElement("a", { className: classnames('brand-list-item', className), href: url }, children));
};
var BrandListItem$1 = React.memo(BrandListItem);

var ExclusiveBrands = function (_a) {
    var children = _a.children;
    return (React.createElement("div", { className: "exclusive-brands" },
        React.createElement("ul", { className: "exclusive-brands__brand-list" }, children)));
};

var ExclusiveBrandsItem = function (_a) {
    var logoSrc = _a.logoSrc, title = _a.title, url = _a.url;
    return (React.createElement("li", { className: "exclusive-brands-item" },
        React.createElement("a", { className: "exclusive-brands-item__link", href: url },
            React.createElement("img", { className: "exclusive-brands-item__image", src: logoSrc, alt: title }))));
};
var ExclusiveBrandsItem$1 = React.memo(ExclusiveBrandsItem);

var SUPPORTED_STYLES = ['primary', 'secondary', 'primary outline', 'secondary outline'];
var ContentBanner = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, title = _a.title, text = _a.text, links = _a.links, _b = _a.textColor, textColor = _b === void 0 ? 'white' : _b;
    var isPhone = MediaQuery.useIsPhone();
    var imageUrl = isPhone ? mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    var imageLink = links.find(function (_a) {
        var style = _a.style;
        return style === 'image';
    });
    var _c = __read(useInView({
        rootMargin: '150px 0px',
        triggerOnce: true,
    }), 2), ref = _c[0], inView = _c[1];
    var btnLinks = React.useMemo(function () {
        return links
            .filter(function (_a) {
            var style = _a.style;
            return SUPPORTED_STYLES.includes(style !== null && style !== void 0 ? style : '');
        })
            .map(function (_a, i) {
            var url = _a.url, label = _a.label, style = _a.style, target = _a.target;
            return (React.createElement(Button, __assign({ key: i, href: url, caption: label, className: "content-banner__button", hrefTarget: target === 'external' ? '_blank' : undefined }, Util.createButtonStyles(style))));
        });
    }, [links]);
    if (!image && !title && !text) {
        console.warn('Cannot render ContentBanner: missing content');
        return null;
    }
    return (React.createElement("div", { ref: ref, className: "content-banner content-banner--color-" + textColor, style: inView && imageUrl ? { backgroundImage: "url(" + imageUrl + ")" } : undefined },
        React.createElement(Container, { className: "content-banner__container" },
            React.createElement("div", { className: "content-banner__body" },
                title && (React.createElement(Heading, { className: "content-banner__title", component: "h2", variant: "large" }, title)),
                text && React.createElement(Text, { className: "content-banner__text" }, text),
                btnLinks,
                imageLink && (React.createElement(Link, { hidden: true, url: imageLink.url, target: imageLink.target, className: "content-banner__image-link" }, imageLink.label && (React.createElement("span", { className: "content-banner__image-link-text" }, imageLink.label))))))));
};

var HeadTags = function (_a) {
    var description = _a.description, ogUrl = _a.ogUrl, ogImage = _a.ogImage, _b = _a.headComponent, Component = _b === void 0 ? React.Fragment : _b, title = _a.title, hreflangs = _a.hreflangs, noFollow = _a.noFollow, noIndex = _a.noIndex, children = _a.children;
    return (React.createElement(Component, null,
        React.createElement("title", null, title),
        ogUrl && React.createElement("link", { rel: "canonical", href: ogUrl }),
        hreflangs.map(function (_a) {
            var url = _a.url, locale = _a.locale;
            return (React.createElement("link", { rel: "alternate", key: url, hrefLang: locale, href: url }));
        }),
        React.createElement("meta", { name: "robots", content: (noIndex ? 'noindex' : 'index') + ", " + (noFollow ? 'nofollow' : 'follow') }),
        React.createElement("meta", { property: "og:url", content: ogUrl }),
        React.createElement("meta", { property: "og:title", content: title }),
        React.createElement("meta", { name: "description", content: description }),
        React.createElement("meta", { property: "og:description", content: description }),
        React.createElement("meta", { name: "twitter:site", content: ogUrl }),
        React.createElement("meta", { name: "twitter:card", content: "summary_large_image" }),
        React.createElement("meta", { name: "twitter:image", content: ogImage }),
        React.createElement("meta", { property: "og:image", content: ogImage }),
        React.createElement("meta", { property: "og:image:width", content: "1200" }),
        React.createElement("meta", { property: "og:image:height", content: "630" }),
        children));
};

var regHex = /#([a-f0-9]{3}){1,2}\b/i;
var regPresetBg = /black|grey|white/;
var RichBanner = function (_a) {
    var _b;
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, richText = _a.richText, layout = _a.layout, bannerWidth = _a.bannerWidth, imageWidth = _a.imageWidth, _c = _a.imageAlignment, imageAlignment = _c === void 0 ? 'left' : _c, _d = _a.backgroundColor, backgroundColor = _d === void 0 ? 'transparent' : _d, _e = _a.textColor, textColor = _e === void 0 ? 'black' : _e, children = _a.children;
    if (!image && !richText) {
        console.warn("Cannot render RichBanner: missing content");
        return null;
    }
    var ContainerComponent = bannerWidth === 'contained' ? Container : React.Fragment;
    var classNames = classnames('rich-banner', "rich-banner--image-alignment-" + imageAlignment, "rich-banner--text-color-" + textColor, (_b = {},
        _b["rich-banner--image-width-" + imageWidth] = imageWidth,
        _b["rich-banner--layout-" + layout] = layout,
        _b));
    var richBannerStyle = {
        backgroundColor: regHex.test(backgroundColor)
            ? backgroundColor
            : regPresetBg.test(backgroundColor)
                ? Util.colorHexMap[backgroundColor]
                : 'transparent',
    };
    var bannerContent = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "rich-banner__content" }, richText && parseHtml(richText)),
        children && React.createElement("div", { className: "rich-banner__children" }, children)));
    return (React.createElement(ContainerComponent, null,
        React.createElement("div", { className: classNames, style: richBannerStyle },
            image && (React.createElement("main", { className: "rich-banner__slot rich-banner__slot--primary" }, link ? (React.createElement(Link, { className: "rich-banner__link", url: link.url, target: link.target },
                link.label && React.createElement("span", { className: "rich-banner__link-text" }, link.label),
                React.createElement(LazyPicture, { className: "rich-banner__picture", mobileImage: mobileImage, image: image }))) : (React.createElement(LazyPicture, { className: "rich-banner__picture", mobileImage: mobileImage, image: image })))),
            richText && (React.createElement("aside", { className: "rich-banner__slot rich-banner__slot--secondary" }, bannerWidth !== 'contained' && imageWidth === '100' ? (React.createElement(Container, null, bannerContent)) : (React.createElement(React.Fragment, null, bannerContent)))))));
};

var CategoryLinks = function (_a) {
    var children = _a.children;
    var isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    if (!children || !Array.isArray(children)) {
        return null;
    }
    return (React.createElement("article", { className: "category-links" },
        React.createElement(Container, null,
            React.createElement("div", { className: "category-links__content" }, isSmallScreen ? React.createElement(Carousel, { pagination: true }, children) : children))));
};

var CategoryLinksItem = function (_a) {
    var title = _a.title, link = _a.link, image = _a.image;
    if (!image && !title) {
        console.warn("Cannot render CategoryLink: missing content");
        return null;
    }
    return (React.createElement("section", { className: "category-links-item" },
        React.createElement("a", { className: "category-links-item__link", href: link.url },
            React.createElement("div", { className: "category-links-item__image-container" }, image && (React.createElement(LazyImage, { rootMargin: "50px 0px", src: image.url, alt: image.alt || '', width: image.dimensions.w || 274, height: image.dimensions.h || 108 }))),
            title && React.createElement("span", { className: "category-links-item__title" }, title))));
};

var Certificates = function (_a) {
    var children = _a.children;
    if (!children) {
        return null;
    }
    return (React.createElement("div", { className: "certificates" },
        React.createElement(Container, null,
            React.createElement("article", { className: "certificates__list" }, children))));
};

var CertificatesItem = function (_a) {
    var _b;
    var link = _a.link, image = _a.image, title = _a.title, text = _a.text;
    if (!image) {
        console.warn("Cannot render Certificate: missing image");
        return null;
    }
    var LinkElement = !!link ? 'a' : 'div';
    return (React.createElement("section", { className: classnames('certificates-item', {
            'certificates-item--center': !title && !text,
        }) },
        React.createElement(LinkElement, { className: "certificates-item__container", href: (link === null || link === void 0 ? void 0 : link.url) || undefined },
            image && (React.createElement("img", { className: "certificates-item__image", src: image.url, alt: (_b = image.alt) !== null && _b !== void 0 ? _b : '' })),
            (title || text) && (React.createElement("div", { className: "certificates-item__content" },
                title && React.createElement("h4", { className: "certificates-item__title" }, title),
                text && React.createElement("p", { className: "certificates-item__description" }, text))))));
};

var PromotionBanners = function (_a) {
    var children = _a.children, _b = _a.fullWidth, fullWidth = _b === void 0 ? false : _b;
    if (!children) {
        return null;
    }
    return (React.createElement("div", { className: classnames('promotion-banners', {
            'promotion-banners--full-width': fullWidth,
        }) },
        React.createElement(Container, { className: "promotion-banners__container" }, children)));
};

var PromotionBannersItem = function (_a) {
    var image = _a.image, mobileImage = _a.mobileImage, link = _a.link, title = _a.title, text = _a.text, _b = _a.fullWidth, fullWidth = _b === void 0 ? false : _b;
    if (!image) {
        console.warn("Cannot render PromotionBanner: missing image");
        return null;
    }
    return (React.createElement("a", { className: classnames('promotion-banners-item', {
            'promotion-banners-item--overlay': title || text,
            'promotion-banners-item--full-width': fullWidth && title && text,
        }), href: link.url },
        image && image.url && (React.createElement(LazyPicture, { className: "promotion-banners-item__image", image: image, mobileImage: mobileImage })),
        React.createElement("div", { className: "promotion-banners-item__body" },
            (title || text) && (React.createElement("div", { className: "promotion-banners-item__content" },
                title && React.createElement("h3", { className: "promotion-banners-item__title" }, title),
                text && React.createElement("p", { className: "promotion-banners-item__description" }, text))),
            link && link.label && (React.createElement(Button, __assign({ size: "custom", caption: link.label, className: "promotion-banners-item__button" }, Util.createButtonStyles(link.style)))))));
};

var Faq = function (_a) {
    var name = _a.name, title = _a.title, ids = _a.ids, children = _a.children;
    var isPhone = MediaQuery.useIsPhone();
    var _b = __read(React.useState(), 2), activeId = _b[0], setActiveId = _b[1];
    var handleTitleClick = React.useCallback(function () {
        history.replaceState(null, '', "#" + name);
    }, [name]);
    // Handle hash from url on mount
    React.useEffect(function () {
        var id = window.location.hash.replace(/^#/, '');
        var hasItem = ids.find(function (idx) { return idx === id; }) || name === id;
        hasItem && setActiveId(id);
    }, []);
    React.useEffect(function () {
        if (activeId) {
            // Push onto callback queue so it runs after the DOM is updated
            setTimeout(function () {
                var node = document.getElementById(activeId);
                if (node) {
                    var headerOffset = 200;
                    var top_1 = node.getBoundingClientRect().top;
                    var to = Math.round(top_1 + window.pageYOffset) - headerOffset;
                    if (to > headerOffset) {
                        window.scrollTo(0, to);
                    }
                }
                // If the active hash is not the title, focus the related te control element
                if (activeId !== name) {
                    var ctrlNode = document.getElementById(activeId + "-control");
                    ctrlNode && ctrlNode.focus();
                }
            }, 250);
        }
    }, [activeId]);
    return (React.createElement(Container, null,
        React.createElement("button", { className: "faq-title-wrapper", onClick: handleTitleClick, "aria-label": title },
            React.createElement(Heading, { id: name, component: "h2", variant: isPhone ? 'small' : 'medium' }, title)),
        React.createElement(Accordion, { className: "faq" }, React.Children.map(children, function (child) { return React.isValidElement(child) && React.cloneElement(child, { activeId: activeId }); }))));
};

var FaqItem = function (_a) {
    var id = _a.id, question = _a.question, answer = _a.answer, activeId = _a.activeId;
    var handleClick = React.useCallback(function (expanded) {
        if (expanded && id) {
            history.replaceState(null, '', "#" + id);
        }
    }, [id]);
    return (React.createElement(AccordionItem, { key: id, id: id, headingLevel: "h3", label: question, expanded: id === activeId, onClick: handleClick }, parseHtml(answer)));
};

var HighlightedContent = function (_a) {
    var children = _a.children, className = _a.className;
    if (!children) {
        return null;
    }
    return (React.createElement("div", { className: classnames('highlighted-content', className) },
        React.createElement("div", { className: "highlighted-content__inner" },
            React.createElement("article", { className: "highlighted-content__items" }, children))));
};

var HighlightedContentItem = function (_a) {
    var image = _a.image, link = _a.link, title = _a.title, text = _a.text;
    if (!image && !text && !title) {
        console.warn("Cannot render USP: missing content");
        return null;
    }
    var LinkElement = !!link ? 'a' : 'div';
    return (React.createElement("section", { className: classnames('highlighted-content-item', {
            'highlighted-content-item__with-link': !!link,
        }) },
        React.createElement(LinkElement, { className: "highlighted-content-item__link", href: (link === null || link === void 0 ? void 0 : link.url) || undefined },
            image && (React.createElement("div", { className: "highlighted-content-item__image", style: { backgroundImage: "url(" + image.url + ")" } })),
            React.createElement("div", { className: "highlighted-content-item__content" },
                title && (React.createElement(Heading, { variant: "medium", className: "highlighted-content-item__title" }, Util.truncateString(title, 50))),
                text && (React.createElement("p", { className: "highlighted-content-item__description" }, Util.truncateString(text, 200)))))));
};

var IconListItem = function (_a) {
    var icon = _a.icon, label = _a.label, url = _a.url;
    var WrapperElement = url ? Link : 'div';
    return (React.createElement("li", { className: "icon-list-item" },
        React.createElement(WrapperElement, { className: "icon-list-item__inner", url: url },
            React.createElement(Icon, { name: icon }),
            label && React.createElement("span", { className: "icon-list-item__text" }, label))));
};

var IconList = function (_a) {
    var title = _a.title, _b = _a.items, items = _b === void 0 ? [] : _b, _c = _a.theme, theme = _c === void 0 ? 'bar' : _c, className = _a.className;
    return (React.createElement("article", { className: classnames('icon-list', className, {
            'icon-list--tiles': theme === 'tiles',
        }) },
        React.createElement("div", { className: "site-container" },
            title && (React.createElement(Heading, { component: "h3", className: "icon-list__title" }, title)),
            React.createElement("ul", { className: "icon-list__items" }, items.map(function (item, index) { return (React.createElement(IconListItem, __assign({}, item, { key: item.icon + "-" + index }))); })))));
};
var IconList$1 = React.memo(IconList);

var UspGroupItem = function (_a) {
    var item = _a.item, className = _a.className;
    var icon = item.icon, text = item.text;
    var classNames = classnames('usp-group-item', className);
    return (React.createElement("div", { className: classNames },
        icon && (React.createElement("span", { className: "usp-group-item__icon" },
            React.createElement(Icon, { name: icon, width: 18, height: 18 }))),
        React.createElement("span", { className: "usp-group-item__message" }, text)));
};

var UspGroup = function (_a) {
    var _b = _a.items, items = _b === void 0 ? [] : _b, theme = _a.theme;
    if (!items.length) {
        return null;
    }
    return (React.createElement("div", { className: classnames('usp-group', {
            'usp-group--solaris': theme === 'sunglasses',
            'usp-group--light': theme === 'light',
        }) },
        React.createElement("div", { className: "usp-group__container desktop-hidden" },
            React.createElement(Carousel, { navigation: true, inverseColor: theme !== 'light', pagination: false }, items.map(function (item) { return (React.createElement(UspGroupItem, { item: item, key: item.text })); }))),
        React.createElement("div", { className: "usp-group__container mobile-hidden" }, items.map(function (item) { return (React.createElement(UspGroupItem, { item: item, key: item.text })); }))));
};

var ReactPlayer = React.lazy(function () { return import(/* webpackChunkName: "react-player" */ 'react-player'); });
var VideoLoader = function () { return (React.createElement("div", { className: "video__loader" },
    React.createElement(SkeletonLoader, { className: "video__skeleton" },
        React.createElement("svg", { fill: "none", xmlns: "http://www.w3.org/2000/svg", height: "100%" },
            React.createElement("rect", { width: "100%", height: "100%", rx: "5", fill: "currentColor" }))))); };
var Video = React.memo(function (_a) {
    var videoUrl = _a.videoUrl, coverImage = _a.coverImage;
    var getLabel = useIntl().getLabel;
    var playerConfig = {
        height: '100%',
        width: '100%',
        controls: true,
        playing: true,
    };
    var _b = __read(React.useState(false), 2), playing = _b[0], setPlaying = _b[1];
    var handleClick = React.useCallback(function () { return setPlaying(true); }, [setPlaying]);
    if (!videoUrl || !coverImage) {
        return null;
    }
    return (React.createElement("div", { className: "video" },
        React.createElement("div", { className: "video__wrapper" }, playing ? (React.createElement("div", { className: "video__player" },
            React.createElement(React.Suspense, { fallback: React.createElement(VideoLoader, null) },
                React.createElement(ReactPlayer, __assign({ url: videoUrl }, playerConfig))))) : (React.createElement("div", { className: "video__image", style: { backgroundImage: "url(" + (coverImage.url || '') + ")" } },
            React.createElement("button", { "aria-label": getLabel('common.play'), className: "video__play-button", onClick: handleClick },
                React.createElement(Icon, { name: "play" })))))));
});

export { Banner, BlogCard$1 as BlogCard, BlogDetailHeader, BlogHero$1 as BlogHero, BlogIntro, BlogList, BlogMeta, BrandList, BrandListGroup, BrandListItem$1 as BrandListItem, CategoryLinks, CategoryLinksItem, Certificates, CertificatesItem, ContentBanner, ExclusiveBrands, ExclusiveBrandsItem$1 as ExclusiveBrandsItem, Faq, FaqItem, HeadTags, HighlightedContent, HighlightedContentItem, IconList$1 as IconList, IconListItem, LazyPicture, Picture, PromotionBanners, PromotionBannersItem, RichBanner, UspGroup, Video };
//# sourceMappingURL=index.es.js.map
